/**
 * Auth-token durability layer.
 *
 * Tokens (accessToken, idToken, refreshToken, authUser, hasOnboarded, etc.)
 * live in localStorage for synchronous access from ~80 call sites across the
 * codebase. On native, localStorage is backed by WKWebView storage which is
 * cleared under storage pressure or after long inactivity — users get silently
 * logged out.
 *
 * This module mirrors writes to @capacitor/preferences (UserDefaults on iOS,
 * SharedPreferences on Android), which survives storage pressure and app
 * updates. The mirror is transparent: existing localStorage.setItem /
 * removeItem call sites don't need to change.
 *
 * On app boot, `hydrateAuthFromPreferences()` runs once before React mounts.
 * If localStorage is empty but Preferences has tokens (e.g. WKWebView cleared
 * localStorage but Preferences survived), it restores them.
 *
 * No-op on web — browsers don't aggressively evict localStorage and
 * Preferences would fall back to localStorage anyway.
 */

import { Preferences } from '@capacitor/preferences';
import { isNativePlatform } from '@/lib/platform';
import { debug, error } from '@/utils/logger';

const AUTH_KEYS = [
    'accessToken',
    'idToken',
    'refreshToken',
    'authUser',
    'isLoggedIn',
    'hasOnboarded',
    'isLogin',
] as const;

const AUTH_KEY_SET: ReadonlySet<string> = new Set(AUTH_KEYS);

let installed = false;

/**
 * Wraps window.localStorage.setItem / removeItem / clear on native so that
 * writes touching any AUTH_KEYS are also persisted to @capacitor/preferences.
 * Call once at app start (before React mounts).
 */
export function installAuthStorageMirror(): void {
    if (installed) return;
    if (typeof window === 'undefined') return;
    if (!isNativePlatform()) return;

    try {
        const storage = window.localStorage;
        const originalSetItem = storage.setItem.bind(storage);
        const originalRemoveItem = storage.removeItem.bind(storage);
        const originalClear = storage.clear.bind(storage);

        storage.setItem = function (key: string, value: string) {
            originalSetItem(key, value);
            if (AUTH_KEY_SET.has(key)) {
                void Preferences.set({ key, value }).catch((e) => {
                    error('[authStorage] Failed to mirror setItem to Preferences', { key, error: e });
                });
            }
        };

        storage.removeItem = function (key: string) {
            originalRemoveItem(key);
            if (AUTH_KEY_SET.has(key)) {
                void Preferences.remove({ key }).catch((e) => {
                    error('[authStorage] Failed to mirror removeItem to Preferences', { key, error: e });
                });
            }
        };

        storage.clear = function () {
            originalClear();
            for (const key of AUTH_KEYS) {
                void Preferences.remove({ key }).catch(() => {
                    /* best effort */
                });
            }
        };

        installed = true;
        debug('[authStorage] Mirror installed (localStorage → Preferences for auth keys)');
    } catch (e) {
        // Some Android WebView versions may reject monkey-patching Storage methods.
        // Keep app startup resilient even when the mirror cannot be installed.
        error('[authStorage] Mirror install failed; continuing without Preferences sync', e);
    }
}

/**
 * If WKWebView cleared localStorage but Preferences still holds the tokens
 * from a prior session, copy them back into localStorage so AuthContext's
 * synchronous init reads them. Must run before React mounts.
 */
export async function hydrateAuthFromPreferences(): Promise<void> {
    if (typeof window === 'undefined') return;
    if (!isNativePlatform()) return;

    // Skip if localStorage already has the primary credential — the mirror
    // keeps it in sync on subsequent writes, so this is the cheap fast path
    // for the common case.
    if (window.localStorage.getItem('accessToken')) {
        return;
    }

    try {
        let restored = 0;
        for (const key of AUTH_KEYS) {
            const { value } = await Preferences.get({ key });
            if (value != null) {
                window.localStorage.setItem(key, value);
                restored++;
            }
        }
        if (restored > 0) {
            debug(`[authStorage] Restored ${restored} auth key(s) from Preferences`);
        }
    } catch (e) {
        error('[authStorage] hydrateAuthFromPreferences failed', e);
    }
}
